/**
 * Keeps the head in sync with the current route.
 *
 * The prerendered HTML already carries the right tags for the URL that was
 * loaded (see `scripts/vite-plugin-html-meta.ts`); this handles what happens
 * after, when react-router swaps pages without a document load. It replaces the
 * `data-seo` block wholesale rather than patching individual tags, so a page
 * never inherits a tag from the page before it.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { pageTags } from './meta';

/** Configured origin, so canonical URLs don't depend on the host that served the app. */
const CONFIGURED_SITE_URL = import.meta.env.VITE_SITE_URL?.trim();

export function useDocumentMeta(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const siteUrl = (CONFIGURED_SITE_URL || window.location.origin).replace(/\/+$/, '');
    const { title, tags } = pageTags(pathname, siteUrl);

    document.title = title;
    for (const node of document.head.querySelectorAll('[data-seo]')) node.remove();

    const fragment = document.createDocumentFragment();
    for (const { tag, attrs } of tags) {
      const element = document.createElement(tag);
      for (const [name, value] of Object.entries(attrs)) element.setAttribute(name, value);
      element.setAttribute('data-seo', '');
      fragment.append(element);
    }
    document.head.append(fragment);
  }, [pathname]);
}
